// KpiSelector component
// CREATED: Dropdown to link a numeric task item to a KPI definition, with inline create option
// UPDATED: Replaced native <select> with a custom dropdown for visual consistency;
//          "Créer un indicateur" option visually separated and styled in teal
// UPDATED: Added max-w-xs to limit dropdown width (KPI names are short, full-width was too wide)
// FIXED: loadKpis now sets loadError state on failure and shows inline error UI instead of silent empty dropdown
// FIXED: handleCreate has a re-entry guard (if creating return) and try/finally so setCreating(false) always runs

import { useState, useEffect, useRef } from 'react';
import { TrendingUp, Plus, X, Loader2, Check, ChevronDown } from 'lucide-react';
import { getKpiDefinitions, createKpiDefinition, type KpiDefinition } from '../../lib/kpi';

interface KpiSelectorProps {
  value: string | null;
  onChange: (kpiId: string | null, kpiName?: string) => void;
  disabled?: boolean;
}

export function KpiSelector({ value, onChange, disabled = false }: KpiSelectorProps) {
  const [kpis, setKpis] = useState<KpiDefinition[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newKpiName, setNewKpiName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadKpis();
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const loadKpis = async () => {
    setLoading(true);
    setLoadError(false);
    const result = await getKpiDefinitions();
    if (result.success) {
      setKpis(result.data);
    } else {
      setLoadError(true);
    }
    setLoading(false);
  };

  const handleSelect = (kpiId: string | null) => {
    if (kpiId === null) {
      onChange(null);
    } else {
      const kpi = kpis.find(k => k.id === kpiId);
      onChange(kpiId, kpi?.name);
    }
    setIsOpen(false);
  };

  const handleOpenCreateForm = () => {
    setIsOpen(false);
    setShowCreateForm(true);
  };

  const handleCreate = async () => {
    if (creating) return;
    const trimmed = newKpiName.trim();
    if (!trimmed) {
      setCreateError("Le nom est obligatoire");
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      const result = await createKpiDefinition(trimmed);
      if (result.success) {
        setKpis(prev => [...prev, result.data].sort((a, b) => a.name.localeCompare(b.name)));
        onChange(result.data.id, result.data.name);
        setShowCreateForm(false);
        setNewKpiName('');
      } else {
        setCreateError(result.error);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
    setNewKpiName('');
    setCreateError(null);
  };

  const selectedKpi = kpis.find(k => k.id === value);

  if (showCreateForm) {
    return (
      <div className="mt-2 p-2.5 bg-teal-50 border border-teal-200 rounded-lg">
        <p className="text-xs font-medium text-teal-800 mb-2">Nouvel indicateur</p>
        <input
          type="text"
          value={newKpiName}
          onChange={e => { setNewKpiName(e.target.value); setCreateError(null); }}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleCreate(); } if (e.key === 'Escape') handleCancelCreate(); }}
          placeholder="Ex : Fréquentation, Chiffre d'affaires..."
          autoFocus
          className="w-full px-2.5 py-1.5 text-sm border border-teal-300 rounded focus:outline-none focus:ring-2 focus:ring-teal-400 bg-white"
        />
        {createError && <p className="text-xs text-red-600 mt-1">{createError}</p>}
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating || !newKpiName.trim()}
            className="flex items-center gap-1 px-2.5 py-1 bg-teal-600 text-white text-xs font-medium rounded hover:bg-teal-700 disabled:opacity-50 transition-colors"
          >
            {creating ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}
            Créer
          </button>
          <button
            type="button"
            onClick={handleCancelCreate}
            className="flex items-center gap-1 px-2.5 py-1 border border-gray-300 text-gray-600 text-xs rounded hover:bg-gray-50 transition-colors"
          >
            <X size={12} />
            Annuler
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 max-w-xs" ref={dropdownRef}>
      <div className="flex items-center gap-1.5 mb-1">
        <TrendingUp size={12} className="text-teal-600" />
        <span className="text-xs text-gray-600 font-medium">Lier à un indicateur</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-1.5 text-xs text-gray-400 py-1">
          <Loader2 size={12} className="animate-spin" />
          Chargement...
        </div>
      ) : loadError ? (
        <div className="flex items-center gap-2 py-1">
          <p className="text-xs text-red-600">Impossible de charger les indicateurs.</p>
          <button
            type="button"
            onClick={loadKpis}
            className="text-xs text-red-600 underline hover:no-underline"
          >
            Réessayer
          </button>
        </div>
      ) : (
        <div className="relative">
          <button
            type="button"
            onClick={() => !disabled && setIsOpen(prev => !prev)}
            disabled={disabled}
            className="w-full flex items-center justify-between px-2.5 py-1.5 text-sm border border-gray-300 rounded bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <span className={selectedKpi ? 'text-gray-900' : 'text-gray-400'}>
              {selectedKpi ? selectedKpi.name : '— Aucun indicateur —'}
            </span>
            <ChevronDown size={14} className={`text-gray-400 transition-transform duration-150 ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {isOpen && (
            <div className="absolute top-full mt-1 left-0 right-0 bg-white border border-gray-200 rounded-lg shadow-lg z-30 max-h-48 overflow-y-auto py-1">
              <button
                type="button"
                onClick={() => handleSelect(null)}
                className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors text-left"
              >
                <span className="text-sm text-gray-500">— Aucun indicateur —</span>
                {value === null && <Check size={14} className="text-primary-600 shrink-0" />}
              </button>

              {kpis.map(kpi => (
                <button
                  key={kpi.id}
                  type="button"
                  onClick={() => handleSelect(kpi.id)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors text-left"
                >
                  <span className="text-sm text-gray-800">{kpi.name}</span>
                  {value === kpi.id && <Check size={14} className="text-primary-600 shrink-0" />}
                </button>
              ))}

              <div className="border-t border-gray-100 mt-1 pt-1">
                <button
                  type="button"
                  onClick={handleOpenCreateForm}
                  className="w-full flex items-center gap-2 px-3 py-2 hover:bg-teal-50 transition-colors text-left"
                >
                  <Plus size={14} className="text-teal-600 shrink-0" />
                  <span className="text-sm font-medium text-teal-600">Créer un indicateur...</span>
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
